/**
 * ScopeAppliedBadge — indica o escopo efetivo aplicado a uma listagem de chamados.
 *
 * Spec: docs/CHAMADOS_VISIBILIDADE_SPEC.md §9.3.2
 *
 * Comportamento:
 *  - Cores: own=gray, org=indigo, global=grape.
 *  - Esconde se scope='own' E sem downgradeReason (ruido visual desnecessario).
 *  - downgradeReason exibido via Tooltip + title (acessibilidade).
 *  - orgName concatenado ao label se scope='org' e fornecido.
 */
import { Badge, Tooltip } from '@mantine/core'
import type { ChamadoScope } from '@/hooks/useScope'

export interface ScopeAppliedBadgeProps {
  scope: ChamadoScope
  downgradeReason?: string | null
  orgName?: string | null
}

const LABELS: Record<ChamadoScope, string> = {
  global: 'Global',
  org: 'Organização',
  own: 'Meus',
}

const COLORS: Record<ChamadoScope, string> = {
  global: 'grape',
  org: 'indigo',
  own: 'gray',
}

export function ScopeAppliedBadge({
  scope,
  downgradeReason,
  orgName,
}: ScopeAppliedBadgeProps) {
  // Esconde se 'own' e sem downgrade — evita poluicao visual no caso comum.
  if (scope === 'own' && !downgradeReason) return null

  const baseLabel =
    scope === 'org' && orgName ? `Org: ${orgName}` : `Escopo: ${LABELS[scope]}`
  const fullLabel = downgradeReason ? `${baseLabel} (rebaixado)` : baseLabel

  const badge = (
    <Badge
      color={COLORS[scope]}
      variant="light"
      size="sm"
      title={downgradeReason ?? undefined}
      aria-label={fullLabel}
    >
      {fullLabel}
    </Badge>
  )

  if (downgradeReason) {
    return (
      <Tooltip label={`Motivo do rebaixamento: ${downgradeReason}`} withArrow>
        {badge}
      </Tooltip>
    )
  }
  return badge
}
